import { Component, EventEmitter, HostListener, Input, Output } from '@angular/core'
import { ApiService } from 'src/app/services/api.service'
import { NetworkSandboxService, SyncPhaseDto } from 'src/app/services/dtos'
import { UiService } from 'src/app/services/ui.service'
import { AccountService } from 'src/app/account/services/account.service'
import { Application, ApplicationProviderService } from 'src/app/account/services/application.service'
import { LaunchRestoreSettings, LaunchSyncDialogBase } from 'src/app/account/components/launch-sync-dialog-base'
import { FormBuilder, Validators } from '@angular/forms'
import { ValidationOutput } from 'src/app/shared/string-list-control/string-list-control.component'
import { RpSliderChangedEvent } from 'src/app/shared/rp-range-slider/rp-range-slider.component'
import { validCidr, validDomain } from '../../../shared/utils'
import { AccessLevelService, AppPermission } from 'src/app/services/access-level.service'

interface Suggestion {
  value: string
  reason: string
}

// Frozen copy of the Review-section data captured at click time. Lets the
// "Launching..." view render exactly what the user just confirmed, even after
// the optimistic syncPhase override on Application drops just-launched apps
// out of the live selectedApplications-derived getters.
interface LaunchTestReviewSnapshot {
  apps: Array<{ name: string; rpTimeString: string | null; showLock: boolean }>
  showNetworkSandboxSection: boolean
  networkSandboxOn: boolean
  isAzure: boolean
  effectiveAdvancedNetworkSandbox: boolean
  networkSandboxAllowedDomains: string[]
  networkSandboxWhitelist: string[]
  prettyAllowedAwsServices: string[]
  showAdvanced: boolean
}

const DEFAULT_AWS_SUGGESTED_DOMAINS: Suggestion[] = [{ value: '.amazonaws.com', reason: 'All AWS services' }]
const DEFAULT_AZURE_SUGGESTED_DOMAINS: Suggestion[] = [
  { value: 'management.azure.com', reason: 'Azure resource management APIs' },
  { value: '.vault.azure.net', reason: 'All Azure key vaults' },
  { value: '.blob.core.windows.net', reason: 'All Azure storage account blobs' },
]

@Component({
  selector: 'launch-test-dialog',
  templateUrl: './launch-test-dialog.component.html',
  styleUrls: ['./launch-test-dialog.component.scss'],
  standalone: false,
})
export class LaunchTestDialogComponent extends LaunchSyncDialogBase {
  @Input() currentlySyncing: boolean = false
  @Output() launch = new EventEmitter<Array<LaunchRestoreSettings>>()

  // No manual "is open" guard — this component is only instantiated while
  // the modal is rendered (inside modal-dialog's *ngIf/ngTemplate).
  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKeydown(event: KeyboardEvent): void {
    if (!this.isTopmostModal()) return
    if (this.launching) return
    event.preventDefault()
    this.onCancelClick()
  }

  public whitelistPlaceholderText = '8.8.8.0/24, 34.0.0.8...'
  public domainAllowlistPlaceholderText = 'registry.hub.docker.com, quay.io'
  public advancedNetworkSandbox: boolean = false
  // Ephemeral toggle for the in-step Advanced Options disclosure; not persisted.
  public advancedOpen: boolean = false
  public sandboxFrozen: boolean = false
  public suggestedDomains: Suggestion[] = []
  public launchingSnapshot: LaunchTestReviewSnapshot | null = null

  private _whitelistIsValid: boolean = true
  private _domainAllowlistIsValid: boolean = true
  constructor(
    private formBuilder: FormBuilder,
    applicationProviderService: ApplicationProviderService,
    accountService: AccountService,
    apiService: ApiService,
    uiService: UiService,
    accessLevelService: AccessLevelService
  ) {
    super(applicationProviderService, accountService, apiService, uiService, accessLevelService)
  }

  launchTestSettingsFormModel = this.formBuilder.group({
    sandboxChecked: [false, Validators.required],
    // Use Array<string> here instead of [] to convey type :)
    sandboxWhitelist: [new Array<string>()],
    sandboxAllowedDomains: [new Array<string>()],
    allowS3Checked: [false],
    allowDynamoChecked: [false],
    forceNewTargetPlans: [false],
  })

  ngOnInit() {
    super.ngOnInit()
    // The new test modal merges the apps + RP picker into one combined step,
    // followed by sandbox (if enabled) and a final review step. Override the
    // base default ('applications') so the first step opens correctly.
    this.activeStepKey = 'app-recovery'
    this.advancedNetworkSandbox = this.application.advancedNetworkSandboxEnabled
    this.launchTestSettingsFormModel.setValue({
      sandboxChecked: this.application.networkSandboxEnabled,
      sandboxWhitelist: this.application.networkSandboxWhitelist,
      sandboxAllowedDomains: this.application.networkSandboxAllowedDomains,
      allowS3Checked: this.application.networkSandboxAllowedServices.includes(NetworkSandboxService.AWS_S3),
      allowDynamoChecked: this.application.networkSandboxAllowedServices.includes(NetworkSandboxService.AWS_DYNAMODB),
      forceNewTargetPlans: false,
    })

    const allowedDomains = new Set(this.networkSandboxAllowedDomains || [])
    const defaultDomains = this.isAzureApplication ? DEFAULT_AZURE_SUGGESTED_DOMAINS : DEFAULT_AWS_SUGGESTED_DOMAINS
    this.suggestedDomains = defaultDomains.filter((sd) => !allowedDomains.has(sd.value))

    this.syncSandboxAvailability()
  }

  // Reactive forms ignore [attr.disabled] — the control's own disabled state
  // is the source of truth, so we drive it from canSandboxRecoveryPoint here.
  // Also clears a stale ON value so we never ship sandbox settings for an RP
  // that can't be sandboxed. sandboxFrozen wins (a sibling app already in
  // FAILOVER_TEST locks the control via applicationIsCandidate).
  private syncSandboxAvailability(): void {
    if (this.sandboxFrozen) return
    const ctrl = this.launchTestSettingsFormModel.get('sandboxChecked')
    if (!ctrl) return
    if (this.canSandboxRecoveryPoint) {
      if (ctrl.disabled) ctrl.enable({ emitEvent: false })
    } else {
      if (ctrl.value) ctrl.setValue(false, { emitEvent: false })
      if (ctrl.enabled) ctrl.disable({ emitEvent: false })
    }
  }

  override recoveryPointChanged(rpSliderChangeEvent: RpSliderChangedEvent) {
    super.recoveryPointChanged(rpSliderChangeEvent)
    this.syncSandboxAvailability()
  }

  override toggleApplicationSelection(applicationId: string) {
    super.toggleApplicationSelection(applicationId)
    this.syncSandboxAvailability()
  }

  // The eager sync in ngOnInit runs while the per-app RP refresh is still in
  // flight, so canSandboxRecoveryPoint falls closed (no stats yet) and the
  // form control gets disabled. Re-sync once each refresh resolves so the
  // checkbox becomes interactive without making the user toggle the app first.
  protected override onRecoveryPointsRefreshed(_application: Application): void {
    this.syncSandboxAvailability()
  }

  override toggleSelectAllApplications(checked: boolean): void {
    super.toggleSelectAllApplications(checked)
    this.syncSandboxAvailability()
  }

  onLaunchClick(): void {
    // Capture the Review-section data BEFORE emit. updateApplicationSyncPhases$
    // sets the optimistic syncPhase override synchronously, which makes
    // applicationIsCandidate filter the just-launched apps out of the live
    // getters — so any binding read after emit would flash empty.
    this.launchingSnapshot = this.buildLaunchingSnapshot()

    this.launch.emit(
      this.selectedApplications.reduce((accum, app) => {
        let rp = this.computeApplicationRecoveryPoint(app)
        if (rp) {
          accum.push({
            application: app,
            recoveryPointId: rp.recoveryPointId,
            isRealTime: this.isRealTime && app.rpo === 0,
            networkSandboxEnabled: this.networkSandboxChecked && this.canSandboxRecoveryPoint,
            networkSandboxWhitelist: this.networkSandboxWhitelist,
            networkSandboxAllowedDomains: this.networkSandboxAllowedDomains,
            networkSandboxAllowedServices: this.networkSandboxAllowedServices,
            advancedNetworkSandbox: this.effectiveAdvancedNetworkSandbox,
            forceNewTargetPlan: this.forceNewTargetPlans,
          } as LaunchRestoreSettings)
        }
        return accum
      }, [] as Array<LaunchRestoreSettings>)
    )
  }

  private buildLaunchingSnapshot(): LaunchTestReviewSnapshot {
    return {
      apps: this.selectedApplications.map((app) => {
        const rp = this.computeApplicationRecoveryPoint(app)
        return {
          name: app.name,
          rpTimeString: rp ? this.recoversToTimeString(app, rp) : null,
          showLock: !!rp && this.showLock(rp),
        }
      }),
      showNetworkSandboxSection: this.accountService.networkSandboxEnabled,
      networkSandboxOn: this.networkSandboxChecked && this.canSandboxRecoveryPoint,
      isAzure: this.isAzureApplication,
      effectiveAdvancedNetworkSandbox: this.effectiveAdvancedNetworkSandbox,
      networkSandboxAllowedDomains: [...(this.networkSandboxAllowedDomains || [])],
      networkSandboxWhitelist: [...(this.networkSandboxWhitelist || [])],
      prettyAllowedAwsServices: [...this.prettyAllowedAwsServices],
      showAdvanced: this.showAdvancedOptions && this.forceNewTargetPlans,
    }
  }

  protected applicationIsCandidate(application: Application) {
    // If another app in the same sync pair (or region pair for multi) is in FAILOVER_TEST
    // then we cannot change the network sandbox behaviour. The network sandbox
    // for this application will be set to be the same as the existing
    // application in FAILOVER_TEST
    if (application.syncPhase === SyncPhaseDto.FAILOVER_TEST) {
      this.sandboxFrozen = true
      this.launchTestSettingsFormModel.setValue({
        sandboxChecked: application.networkSandboxEnabled,
        sandboxWhitelist: application.networkSandboxWhitelist,
        sandboxAllowedDomains: application.networkSandboxAllowedDomains,
        allowS3Checked: application.networkSandboxAllowedServices.includes(NetworkSandboxService.AWS_S3),
        allowDynamoChecked: application.networkSandboxAllowedServices.includes(NetworkSandboxService.AWS_DYNAMODB),
        forceNewTargetPlans: false,
      })
      // The disable here doesn't work for the whitelist,
      // so we do it inside string-list-control
      this.launchTestSettingsFormModel.get('sandboxChecked').disable()
      this.launchTestSettingsFormModel.get('allowS3Checked').disable()
      this.launchTestSettingsFormModel.get('allowDynamoChecked').disable()
    }
    return application.syncPhase === SyncPhaseDto.STANDBY
  }

  get networkSandboxChecked(): boolean {
    return this.launchTestSettingsFormModel.get('sandboxChecked').value
  }

  get networkSandboxWhitelist(): string[] {
    return this.launchTestSettingsFormModel.get('sandboxWhitelist').value
  }

  get networkSandboxAllowedDomains(): string[] {
    return this.launchTestSettingsFormModel.get('sandboxAllowedDomains').value
  }

  get networkSandboxAllowedServices(): string[] {
    let allowedServices = []

    if (this.launchTestSettingsFormModel.get('allowS3Checked').value) {
      allowedServices.push(NetworkSandboxService.AWS_S3)
    }

    if (this.launchTestSettingsFormModel.get('allowDynamoChecked').value) {
      allowedServices.push(NetworkSandboxService.AWS_DYNAMODB)
    }

    return allowedServices
  }

  get forceNewTargetPlans(): boolean {
    return this.launchTestSettingsFormModel.get('forceNewTargetPlans').value
  }

  public isValidCidr(candidate: string): boolean {
    return validCidr(candidate)
  }

  public isValidDomain(domain: string): boolean {
    return validDomain(domain)
  }

  public normalizeCidr(candidate: string): string {
    return candidate.includes('/') ? candidate : candidate + '/32'
  }

  whitelistValidated(event: ValidationOutput): void {
    this._whitelistIsValid = !event.invalid
  }

  sandboxDomainsValidated(event: ValidationOutput): void {
    this._domainAllowlistIsValid = !event.invalid
  }

  setAdvancedNetworkSandbox(value: boolean): void {
    this.advancedNetworkSandbox = value
  }

  // Single source of truth for whether the Advanced (Network Firewall) mode is
  // effectively active. Resolves the user's intent against what the selected RP
  // actually supports:
  //   - Azure → no AWS-style basic/advanced distinction; honor user intent.
  //   - Only one option available → force that option (user intent ignored).
  //   - Both available → honor user intent (advancedNetworkSandbox).
  //   - Neither available → canSandboxRecoveryPoint is false and the whole
  //     sandbox is disabled upstream, so the return value here is moot.
  // Preserves user intent if they slide across RPs where availability changes.
  get effectiveAdvancedNetworkSandbox(): boolean {
    if (this.isAzureApplication) return this.advancedNetworkSandbox
    if (!this.shouldShowAdvancedSandboxOption) return false
    if (!this.shouldShowBasicSandboxOption) return true
    return this.advancedNetworkSandbox
  }

  get showAdvancedSandboxOptions(): boolean {
    return this.accountService.advancedNetworkSandboxEnabled && this.networkSandboxChecked
  }

  // True if any selected app has the resources needed to sandbox the launch:
  // AWS needs an SG or IGW; Azure needs a VNet. Falls closed when no selected
  // app has stats (all legacy RPs) — hide the option until we can verify.
  get canSandboxRecoveryPoint(): boolean {
    if (!this.selectedAppsHaveAnyStats) return false
    if (this.isAzureApplication) return this.selectedAppsHaveAnyVnet
    return this.selectedAppsHaveAnySg || this.selectedAppsHaveAnyIgw
  }

  get sandboxUnavailableReason(): string {
    if (this.isAzureApplication) {
      return 'No selected application has a virtual network in its recovery point, so network sandboxing is not available.'
    }
    return 'No selected application has a security group or internet gateway in its recovery point, so network sandboxing is not available.'
  }

  // Either no selected app supports sandboxing, or a sibling app is already in
  // a test and the settings are inherited from that test (sandboxFrozen).
  get sandboxSectionDisabled(): boolean {
    return this.sandboxFrozen || !this.canSandboxRecoveryPoint
  }

  get sandboxDisabledReason(): string {
    if (this.sandboxFrozen) {
      return 'Another application in this sync pair is already in test — sandbox settings are inherited and can’t be changed.'
    }
    return this.sandboxUnavailableReason
  }

  get showAdvancedOptions(): boolean {
    return this.accountService.entitledForCapability('allowForceNewTargetPlan')
  }

  get canLaunchTest(): boolean {
    // Shared base gating: selected apps + resolvable RPs + not launching.
    if (!this.canLaunch) return false
    // if appGroupRestore, then check application is in standby, and
    if (this.accountService.entitledForCapability('appGroupRestore')) {
      return (
        (!this.currentlySyncing || this.application.syncPhase === SyncPhaseDto.STANDBY) &&
        this._whitelistIsValid &&
        this._domainAllowlistIsValid
      )
    }
    // Else default to previous behaviour
    return !this.currentlySyncing && this._whitelistIsValid
  }

  // Sandbox is the only optional step between apps+RP and review. When the
  // account doesn't have it, skip straight to review so the wizard never
  // dead-ends on an absent step.
  get nextStepAfterAppRecovery(): string {
    return this.accountService.networkSandboxEnabled ? 'sandbox' : 'review'
  }

  // Selected apps that don't resolve to a usable RP at the current slider
  // position. Surfaced in the Review step so the user can see (and fix)
  // missing coverage before launching — addresses the original ARP-6987
  // complaint that this state was hidden behind a collapsed apps section.
  get appsMissingRecoveryPoint(): Array<Application> {
    return this.selectedApplications.filter((app) => !this.computeApplicationRecoveryPoint(app))
  }

  // Friendly labels for the allowed AWS services pills shown in Review —
  // the underlying enum values ('aws:s3', 'aws:dynamodb') aren't user-facing.
  get prettyAllowedAwsServices(): string[] {
    return this.networkSandboxAllowedServices.map((s) => {
      if (s === NetworkSandboxService.AWS_S3) return 'S3'
      if (s === NetworkSandboxService.AWS_DYNAMODB) return 'DynamoDB'
      return s
    })
  }

  get currentlySyncingMessage() {
    if (this.accountService.entitledForCapability('appGroupRestore')) {
      return this.currentlySyncing && this.application.syncPhase !== SyncPhaseDto.STANDBY
    }
    return this.currentlySyncing
  }

  addAllowedDomain(domain: string) {
    const control = this.launchTestSettingsFormModel.get('sandboxAllowedDomains')
    let value = control.value || []
    value.push(domain)
    control.setValue(value)
    this.suggestedDomains = this.suggestedDomains.filter((s) => s.value != domain)
  }

  protected getRequiredPermission(): AppPermission {
    return AppPermission.MANAGE_TEST
  }

  get isAzureApplication(): boolean {
    return this.application?.type === 'azure'
  }
}
